import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { IManualDeliverableStatus } from '@interfaces/campaign-analytics-api.interface';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { IManualStatusFetchParams } from './cta-analytics-details-deliverable-card/cta-analytics-details-deliverable-card.interface';
import { ManualDeliverableStatusService } from './cta-analytics-details-deliverable-card/manual-deliverable-status/manual-deliverable-status.service';

interface ICtaAnalyticsDetailsState {
  error: string | null;
  loading: boolean;
  manualDeliverableStatuses: Map<string, IManualDeliverableStatus> | null;
}

const initialState: ICtaAnalyticsDetailsState = {
  error: null,
  loading: false,
  manualDeliverableStatuses: null,
};

@Injectable({
  providedIn: 'root',
})
export class CtaAnalyticsDetailsStore extends ComponentStore<ICtaAnalyticsDetailsState> {
  readonly manualDeliverableStatuses$ = this.selectSignal((s) => s.manualDeliverableStatuses);
  readonly loading$ = this.selectSignal((s) => s.loading);
  readonly error$ = this.selectSignal((s) => s.error);

  public readonly fetchManualDeliverableStatuses = this.effect((params$: Observable<IManualStatusFetchParams>) =>
    params$.pipe(
      tap(() => this.patchState({ error: null, loading: true, manualDeliverableStatuses: null })),
      switchMap((params) => {
        const httpParams = new HttpParams()
          .set('campaign_id', params.campaignId)
          .set('creator_id', params.creatorId)
          .set('cta_template_id', params.ctaTemplateId)
          .set('social_content_id', params.socialContentId!)
          .set('limit', 1000);

        const request$ = params.campaignExportId
          ? this.deliverableManualStatusService.getManualDeliverableStatusesExport(params.campaignExportId, httpParams)
          : this.deliverableManualStatusService.getManualDeliverableStatuses(httpParams);

        return request$.pipe(
          tap((manualDeliverableStatuses: IApiResponse<IManualDeliverableStatus[]>) => {
            this.patchState({
              loading: false,
              manualDeliverableStatuses: new Map(
                manualDeliverableStatuses.data?.map((status) => [status.deliverableId, status]) ?? [],
              ),
            });
          }),
          catchError(() => {
            this.patchState({
              error: 'Failed to load manual deliverable statuses',
              loading: false,
            });

            return EMPTY;
          }),
        );
      }),
    ),
  );

  constructor(private readonly deliverableManualStatusService: ManualDeliverableStatusService) {
    super(initialState);
  }
}
